import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { getAllFaqItems } from "../data/faqData";
import useInstanceInfo from "../hooks/useInstanceInfo";

const STOP_WORDS = new Set([
  "the", "a", "an", "is", "are", "was", "were", "be", "been", "being",
  "have", "has", "had", "do", "does", "did", "will", "would", "could",
  "should", "may", "might", "shall", "can", "need", "dare", "ought",
  "used", "to", "of", "in", "for", "on", "with", "at", "by", "from",
  "as", "into", "through", "during", "before", "after", "above", "below",
  "between", "out", "off", "over", "under", "again", "further", "then",
  "once", "here", "there", "when", "where", "why", "how", "all", "each",
  "every", "both", "few", "more", "most", "other", "some", "such", "no",
  "nor", "not", "only", "own", "same", "so", "than", "too", "very",
  "just", "because", "but", "and", "or", "if", "while", "about", "up",
  "what", "which", "who", "whom", "this", "that", "these", "those",
  "i", "me", "my", "we", "our", "you", "your", "it", "its", "they",
  "them", "their"
]);

const WELCOME_MESSAGE = {
  role: "bot",
  text: "Hi! I can help answer questions about Axaris. Try asking about assessments, risk scores, user roles, or any platform feature."
};

function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter(w => w.length > 1 && !STOP_WORDS.has(w));
}

function searchFaq(input, supportEmail) {
  const tokens = tokenize(input);
  if (tokens.length === 0) return null;

  const items = getAllFaqItems(supportEmail);
  let bestMatch = null;
  let bestScore = 0;
  let bestQuestionScore = 0;

  for (const item of items) {
    const qText = item.q.toLowerCase();
    const aText = item.a.toLowerCase();
    let score = 0;
    let questionScore = 0;

    for (const token of tokens) {
      if (qText.includes(token)) {
        score += 2;
        questionScore += 2;
      }
      if (aText.includes(token)) {
        score += 1;
      }
    }

    if (score > bestScore || (score === bestScore && questionScore > bestQuestionScore)) {
      bestScore = score;
      bestQuestionScore = questionScore;
      bestMatch = item;
    }
  }

  return bestScore > 0 ? bestMatch : null;
}

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const { info } = useInstanceInfo();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Hide on /organizations or when no organization is selected
  const selectedOrg = localStorage.getItem("selectedOrganization");
  const hasOrg = (() => {
    if (!selectedOrg) return false;
    try {
      return !!JSON.parse(selectedOrg).organizationId;
    } catch { return false; }
  })();

  if (location.pathname === "/organizations" || !hasOrg) return null;

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg = { role: "user", text: trimmed };
    const match = searchFaq(trimmed, info.supportEmail);

    let botMsg;
    if (match) {
      botMsg = {
        role: "bot",
        text: match.a,
        category: match.category,
        question: match.q
      };
    } else {
      botMsg = { role: "bot", text: null, noMatch: true };
    }

    setMessages(prev => [...prev, userMsg, botMsg]);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-primary shadow-glow-cyan flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Open help chat"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[500px] flex flex-col rounded-xl border border-border shadow-theme-lg overflow-hidden bg-theme-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-primary text-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <span className="font-semibold text-sm">Axaris Help Bot</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 rounded hover:bg-white/20 transition-colors"
          aria-label="Close chat"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "user" ? (
              <div className="max-w-[80%] px-3 py-2 rounded-lg bg-accent-cyan/20 text-content-primary text-sm">
                {msg.text}
              </div>
            ) : msg.noMatch ? (
              <div className="max-w-[80%] px-3 py-2 rounded-lg bg-theme-card-solid border border-border text-sm text-content-secondary">
                I couldn't find a specific answer for that. Try rephrasing, or visit the{" "}
                <Link to="/faq" className="text-accent-cyan hover:underline" onClick={() => setIsOpen(false)}>FAQ page</Link>{" "}
                for a full list of topics, or{" "}
                <Link to="/contact" className="text-accent-cyan hover:underline" onClick={() => setIsOpen(false)}>Contact Us</Link>{" "}
                to reach our team.
              </div>
            ) : (
              <div className="max-w-[80%] px-3 py-2 rounded-lg bg-theme-card-solid border border-border text-sm">
                {msg.question && (
                  <p className="text-accent-cyan font-medium mb-1 text-xs">{msg.question}</p>
                )}
                <p className="text-content-primary leading-relaxed">{msg.text}</p>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-3 border-t border-border bg-theme-secondary flex-shrink-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question..."
          className="flex-1 px-3 py-2 rounded-lg bg-theme-tertiary border border-border text-content-primary placeholder-content-muted text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan transition-colors"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className="p-2 rounded-lg bg-gradient-primary text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-glow-cyan transition-all"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ChatWidget;
